import React, { DragEvent, ChangeEvent, useRef, useState } from 'react';

import { Button } from './button';
import { Flex } from './flex';
import { HiddenInput } from './hiddenInput';
import { colorScheme } from '../../theme';

interface FileImportButtonProps {
  /** Text displayed on the button */
  buttonText: string;
  /** Callback when file is selected or dropped */
  onFile: (file: File) => void;
  /** Accepted file types (default: "application/json") */
  accept?: string;
  /** Data test attribute */
  'data-test'?: string;
}

export function FileImportButton({ buttonText, onFile, accept = 'application/json', 'data-test': dataTest }: FileImportButtonProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const isFileAccepted = (file: File): boolean => {
    if (!accept) return true;

    const acceptedTypes = accept.split(',').map((t) => t.trim().toLowerCase());

    return acceptedTypes.some((acceptedType) => {
      if (acceptedType.startsWith('.')) {
        return file.name.toLowerCase().endsWith(acceptedType);
      }

      return file.type.toLowerCase() === acceptedType;
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!isFileAccepted(file)) {
      return;
    }

    setFileName(file.name);
    onFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as Node | null;
    if (!e.currentTarget.contains(relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;

    if (!isFileAccepted(droppedFile)) {
      return;
    }

    setFileName(droppedFile.name);
    onFile(droppedFile);
  };

  const draggingStyle = isDragging
    ? { border: `2px dashed ${colorScheme.brand}`, backgroundColor: colorScheme.buttons.secondary.background }
    : undefined;

  return (
    <Flex align="center" gapSize="md">
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        data-test={dataTest ? `${dataTest}-input` : undefined}
      />
      <Button
        variant="secondary"
        onClick={handleImportClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        title="Drag & drop file here"
        data-test={dataTest}
        style={draggingStyle}
      >
        {buttonText}
      </Button>
      {fileName && <span>Selected File: {fileName}</span>}
    </Flex>
  );
}
